import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import LeadsFiltersPanel from './LeadsFiltersPanel';
import LeadRecordSearchResults from './LeadRecordSearch';
import {
  EMPTY_LEAD_ADVANCED_FILTERS,
  LEAD_DEFAULT_SORT,
  LEAD_SORT_LABELS,
  SHOW_LEAD_PROFILE_FILTERS,
  buildLeadAdvancedQuery,
  countLeadAdvancedFilters,
  describeLeadAdvancedFilters,
  removeLeadProfileFilter,
  withoutLeadProfileFilters,
} from '../leadFilters';
import { filterBadgeStyle } from '../../Map/components/MapHeader/MapHeaderSearchFilters.styles';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import {
  deleteLead,
  getLeadErrorMessage,
  getLeads,
  isLeadProfileDisabled,
  isLeadProfileNotEnabled,
  readApiErrorCode,
  readLeadProfileFilterErrorField,
  readLeadsList,
} from '../../services/leadsService';
import { buildLeadRecordQuery, describeLeadRecordFilter } from '../leadRecordSearch';
import { leadItemChipSx } from './LeadItemChips';
import {
  LEAD_FOLLOWUP_FILTER_OPTIONS,
  LEAD_TEMPERATURES,
  LEAD_TEMPERATURE_LABELS,
  buildLeadFollowUpQuery,
  describeLeadInterestSummary,
  describeManualInquiryCreated,
  normalizeLeadStats,
  resolveLeadTemperatureCounts,
} from '../leadsConfig';
import { formatDateTimeRelativeAR } from '../../Utils/dateTimeFormat';
import { colors, typography } from '../../styles/theme';
import PanelShell from '../../Map/components/PanelShell/PanelShell';
import PanelEmptyState from '../../Components/PanelEmptyState';
import PanelHeader, { panelCloseIconButtonSx } from '../../Map/components/PanelShell/PanelHeader';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import {
  emptyStateBoxSx,
  emptyStateHintSx,
  emptyStateIconBoxSx,
  emptyStateIconSx,
  emptyStateTitleSx,
  listAddButtonSx,
  listSearchFieldSx,
  listSearchIconSx,
  scrollableListZoneSx,
} from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import LeadCreateDialog from './LeadCreateDialog';
import LeadManualInquiryDialog from './LeadManualInquiryDialog';
import { isManualInquiryDisabled } from '../../services/manualInquiryService';
import LeadTemperatureDot from './LeadTemperatureDot';
import LeadDetail from './LeadDetail';
import LeadsSummary from './LeadsSummary';
import LeadCardsSkeleton from './LeadCardsSkeleton';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  LEADS_PANEL_BG,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_SECONDARY,
  LEADS_UNSEEN_BADGE_BG,
  getLeadCardSx,
  getLeadTemperatureFilterChipSx,
  leadCardMetaSx,
  leadCardTitleSx,
  leadsCountBadgeSx,
  leadsGhostButtonSx,
  leadsToolbarSkeletonSx,
  leadsPanelPaperSx,
  leadsSummaryRowSx,
  leadsToolbarZoneSx,
} from './leadsUi.styles';

const PAGE_LIMIT = 200;
const SEARCH_DEBOUNCE_MS = 300;


/** Anchos de los chips de temperatura (Todos + Caliente/Tibio/Frío). */
const TEMP_CHIP_SKELETON_WIDTHS = [72, 96, 80, 88];

function LeadsTemperatureChipsSkeleton() {
  return (
    <Box sx={{ display: 'flex', flex: '1 1 0', minWidth: 0, gap: 0.75 }} aria-hidden>
      {TEMP_CHIP_SKELETON_WIDTHS.map((_, i) => (
        <Skeleton
          key={`temp-skel-${i}`}
          variant="rounded"
          height={30}
          sx={{ ...leadsToolbarSkeletonSx, borderRadius: '6px', flex: '1 1 0', minWidth: 0 }}
        />
      ))}
    </Box>
  );
}

/** Resumen del índice («N leads · …»): depende de la data; va en el lugar de `LeadsSummary`. */
function LeadsSummarySkeleton() {
  return (
    <Box sx={leadsSummaryRowSx} aria-hidden data-testid="leads-summary-skeleton">
      <Skeleton variant="rounded" width={132} height={12} sx={leadsToolbarSkeletonSx} />
    </Box>
  );
}


const LEADS_SUBTITLE = 'Seguí a tus interesados, sus consultas y actividad.';

/** Lead de detalle/alta → forma de la tarjeta (`item_count`, `last_activity_at`). */
function toCardLead(lead, prev = null) {
  const items = Array.isArray(lead?.items) ? lead.items : null;
  const activity = Array.isArray(lead?.activity) ? lead.activity : null;
  const { items: _items, activity: _activity, inquiries: _inquiries, ...rest } = lead || {};
  return {
    ...(prev || {}),
    ...rest,
    item_count: items ? items.length : (prev?.item_count ?? 0),
    last_activity_at: activity?.[0]?.created_at || lead?.updated_at || prev?.last_activity_at || null,
  };
}

const inquiryBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  height: 20,
  px: '6px',
  borderRadius: '10px',
  bgcolor: 'rgba(255,255,255,0.08)',
  color: '#D4D4D4',
  fontSize: '11px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '& .MuiSvgIcon-root': { fontSize: 12 },
};

const unseenInquiryBadgeSx = {
  ...inquiryBadgeSx,
  bgcolor: LEADS_UNSEEN_BADGE_BG,
  color: '#E8F4F4',
};

function LeadCard({ lead, busy, onOpen, onOpenMenu = null }) {
  const contact = [lead.phone, lead.email].filter(Boolean).join(' · ');
  const interestSummary = describeLeadInterestSummary(lead);
  const inquiryCount = Number(lead.inquiry_count) || 0;
  const unseenInquiries = Number(lead.unseen_inquiry_count) || 0;
  // Sin consultas: "Carga manual …" desde created_at. Con ≥1: "Contacto …" (last_touch).
  const when = inquiryCount > 0
    ? formatDateTimeRelativeAR(lead.last_touch_at || lead.last_activity_at || lead.updated_at)
    : formatDateTimeRelativeAR(lead.created_at);
  const whenLine = when
    ? (inquiryCount > 0 ? `Contacto ${when}` : `Carga manual ${when}`)
    : '';
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Abrir ${lead.name}`}
      data-testid="lead-card"
      onClick={() => onOpen(lead.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(lead.id);
        }
      }}
      sx={getLeadCardSx(lead.temperature)}
    >
      <Box data-testid="lead-card-title-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, pr: onOpenMenu ? 3 : 0 }}>
        {/* Nombre ≤ ~50% de la card (ellipsis + title); chip de nuevas a su derecha, sin encogerse. */}
        <Typography
          title={lead.name}
          data-testid="lead-card-name"
          sx={{ ...leadCardTitleSx, minWidth: 0, maxWidth: '50%', flexShrink: 1 }}
        >
          {lead.name}
        </Typography>
        {unseenInquiries > 0 && (
          <Box component="span" sx={unseenInquiryBadgeSx} data-testid="lead-unseen-inquiries">
            {unseenInquiries === 1 ? '1 consulta nueva' : `${unseenInquiries} consultas nuevas`}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <LeadTemperatureDot temperature={lead.temperature} />
      </Box>
      {/* El origen va solo en el detalle (subtítulo + Datos), no en la tarjeta. */}
      {contact && <Typography sx={leadCardMetaSx}>{contact}</Typography>}
      {lead.interest && (
        <Typography sx={{ ...leadCardMetaSx, color: '#C8C8C8' }}>{lead.interest}</Typography>
      )}
      <Typography sx={{ ...leadCardMetaSx, color: LEADS_TEXT_MUTED, fontSize: '11px' }}>
        {[
          interestSummary,
          inquiryCount ? `${inquiryCount} ${inquiryCount === 1 ? 'consulta' : 'consultas'}` : '',
          whenLine,
        ].filter(Boolean).join(' · ')}
      </Typography>
      {onOpenMenu && (
        <IconButton
          size="small"
          aria-label={`Acciones de ${lead.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu(e.currentTarget, lead);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          disabled={busy}
          sx={{
            position: 'absolute',
            top: 6,
            right: 4,
            ...panelCloseIconButtonSx,
            color: LEADS_TEXT_SECONDARY,
          }}
        >
          {busy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <MoreVertIcon />}
        </IconButton>
      )}
    </Box>
  );
}

/**
 * Panel de Leads (L1 + L2). Map: docs/maps/leads.md.
 * Índice con chips por temperatura + búsqueda, alta en diálogo y detalle con "atrás".
 *
 * Espacio ajeno: `ownerUserId` → GET con `?owner_user_id=`, writes con el owner
 * en el body, sin borrar. 403 → estado vacío (no es error de red).
 * MapPage lo monta con `key` por owner: cambiar de espacio lo remonta.
 *
 * L2: chips de seguimiento, badges de consulta y `openLeadRequest`
 * (`{ leadId, ts }`, desde la bandeja) para abrir una ficha directo.
 *
 * Intereses clicables: `onOpenItemTarget` (MapPage) recibe el destino ya
 * resuelto (lote, lista o lista CAD); ver `useLeadItemOpener`.
 *
 * @param {{ ownerUserId?: string|null, uiLeftInset?: number, onClose: () => void,
 *   openLeadRequest?: { leadId: string, ts: number }|null,
 *   onOpenItemTarget?: (target: object) => void,
 *   unitReturn?: { leadId: string, label: string }|null, onUnitReturn?: () => void }} props
 *   `unitReturn`: lead abierto desde una UF; su volver reabre la UF (`onUnitReturn`).
 */
export default function LeadsPanel({
  ownerUserId = null,
  uiLeftInset,
  onClose,
  openLeadRequest = null,
  onOpenItemTarget,
  unitReturn = null,
  onUnitReturn = undefined,
}) {
  const isShared = Boolean(ownerUserId);
  const canDelete = !isShared;

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [reloadSeq, setReloadSeq] = useState(0);

  const [temperatureFilter, setTemperatureFilter] = useState('all');
  // Chips de seguimiento: all | unseen | inactive | offer_reserve.
  const [followUpFilter, setFollowUpFilter] = useState('all');
  // Lote / unidad elegido en el buscador de registros (`item_kind` + `id`).
  const [recordFilter, setRecordFilter] = useState(null);
  const [search, setSearch] = useState('');
  // Buscador único: 'people' (nombre/email/teléfono → q de /getleads) o 'record' (lote o unidad).
  const [searchMode, setSearchMode] = useState('people');
  const [recordText, setRecordText] = useState('');
  const [modeAnchor, setModeAnchor] = useState(null);
  const [query, setQuery] = useState('');
  // Orden server-side (`sort`); la API devuelve la página ya ordenada.
  const [sortBy, setSortBy] = useState(LEAD_DEFAULT_SORT);
  // Filtros avanzados: borrador del panel → `appliedFilters` con debounce.
  const [advancedFilters, setAdvancedFilters] = useState(EMPTY_LEAD_ADVANCED_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_LEAD_ADVANCED_FILTERS);
  const [filtersAnchor, setFiltersAnchor] = useState(null);
  // «Datos del cliente» (`pf_*`): se apaga en la sesión con un 503 LEAD_PROFILE_NOT_ENABLED.
  const [profileFiltersOff, setProfileFiltersOff] = useState(false);
  const showProfileFilters = SHOW_LEAD_PROFILE_FILTERS && !profileFiltersOff && !isLeadProfileDisabled();
  const [totalFiltered, setTotalFiltered] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(() => String(openLeadRequest?.leadId || ''));
  const [createOpen, setCreateOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const deleteAbortRef = useRef(null);
  // Fade arriba / abajo en la lista de leads (mismo uso que Historial).
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  useEffect(() => () => deleteAbortRef.current?.abort(), []);

  useEffect(() => {
    const handle = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(() => setAppliedFilters(advancedFilters), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [advancedFilters]);
  const advancedQuery = useMemo(() => buildLeadAdvancedQuery(appliedFilters), [appliedFilters]);
  const advancedKey = JSON.stringify(advancedQuery);
  const advancedQueryRef = useRef(advancedQuery);
  advancedQueryRef.current = advancedQuery;
  const advancedActive = advancedKey !== '{}';

  const reload = useCallback(() => setReloadSeq((n) => n + 1), []);

  // Pedido de la bandeja con el panel ya abierto.
  const requestTs = openLeadRequest?.ts;
  const requestLeadId = openLeadRequest?.leadId;
  useEffect(() => {
    if (requestLeadId) setOpenLeadId(String(requestLeadId));
  }, [requestTs, requestLeadId]);

  const recordKey = recordFilter ? `${recordFilter.itemKind}:${recordFilter.id}` : '';
  const recordFilterRef = useRef(recordFilter);
  recordFilterRef.current = recordFilter;
  const clearRecordFilter = useCallback(() => setRecordFilter(null), []);
  useEffect(() => {
    const controller = new AbortController();
    setFetching(true);
    (async () => {
      try {
        const followUpQuery = buildLeadFollowUpQuery(followUpFilter);
        const res = await getLeads(
          {
            q: query,
            temperature: temperatureFilter !== 'all' ? temperatureFilter : '',
            ...followUpQuery,
            ...buildLeadRecordQuery(recordFilterRef.current),
            ...advancedQueryRef.current,
            sort: sortBy,
            limit: PAGE_LIMIT,
          },
          { ownerUserId, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (recordFilterRef.current && res?._httpStatus === 404 && readApiErrorCode(res) === 'RECORD_NOT_FOUND') {
          toast.info('Ese registro ya no está en este espacio. Mostramos todos los leads.');
          clearRecordFilter();
          return;
        }
        // 400 en un `pf_*`: aviso y se quita ese filtro (el cambio de estado vuelve a pedir).
        const pfField = readLeadProfileFilterErrorField(res);
        if (pfField) {
          const { label } = removeLeadProfileFilter(null, pfField);
          const detail = typeof res?.error === 'string' && res.error.trim() ? ` (${res.error.trim()})` : '';
          toast.warning(`No se pudo aplicar el filtro de ${label}${detail}. Lo quitamos.`);
          setAdvancedFilters((f) => removeLeadProfileFilter(f, pfField).filters);
          setAppliedFilters((f) => removeLeadProfileFilter(f, pfField).filters);
          return;
        }
        // 503 con `pf_*`: falta la SQL 101 → sin sección «Datos del cliente» en la sesión.
        if (advancedQueryRef.current.profile && isLeadProfileNotEnabled(res)) {
          toast.info('Los filtros por datos del cliente todavía no están disponibles. Los quitamos.');
          setProfileFiltersOff(true);
          setAdvancedFilters(withoutLeadProfileFilters);
          setAppliedFilters(withoutLeadProfileFilters);
          return;
        }
        if (res?._httpOk) {
          const parsed = readLeadsList(res);
          setLeads(parsed.leads);
          setStats(parsed.stats);
          setHasMore(parsed.hasMore);
          setTotalFiltered(parsed.totalFiltered);
          setLoadError('');
          setForbidden(false);
        } else if (res?._httpStatus === 403) {
          setForbidden(true);
          setLeads([]);
          setStats(null);
          setLoadError('');
        } else {
          setLoadError(getLeadErrorMessage(res, 'No se pudieron cargar los leads.'));
        }
        setLoaded(true);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setLoadError('No se pudieron cargar los leads.');
        setLoaded(true);
      } finally {
        if (!controller.signal.aborted) setFetching(false);
      }
    })();
    return () => controller.abort();
  }, [ownerUserId, query, temperatureFilter, followUpFilter, recordKey, reloadSeq, clearRecordFilter, advancedKey, sortBy]);

  // Total del owner: “de N leads”.
  const serverStats = useMemo(() => stats, [stats]);
  const serverCounts = useMemo(() => normalizeLeadStats(serverStats), [serverStats]);
  // Chips de temperatura: stats.by_temperature es global del owner.
  const counts = useMemo(
    () => resolveLeadTemperatureCounts({ leads, hasMore, serverStats }),
    [leads, hasMore, serverStats],
  );
  const followUpActive = followUpFilter !== 'all';
  const followUpLabel = followUpActive
    ? (LEAD_FOLLOWUP_FILTER_OPTIONS.find((o) => o.id === followUpFilter)?.label || '')
    : '';

  // Orden y temperatura: del servidor (`sort` / `temperature=`).
  const visibleLeads = leads;

  const upsertLead = useCallback((lead) => {
    if (!lead?.id) return;
    setLeads((prev) => {
      const idx = prev.findIndex((l) => l.id === lead.id);
      if (idx === -1) return [toCardLead(lead), ...prev];
      const next = prev.slice();
      next[idx] = toCardLead(lead, prev[idx]);
      return next;
    });
  }, []);

  // La ficha cambió algo (etapa, datos, intereses, nota, marcar vistas): al
  // volver se pide getleads de nuevo para que stats / list_stats / unseen y
  // la pertenencia a los filtros salgan del servidor. Mientras tanto los
  // chips ya cuentan la lista local actualizada.
  const detailDirtyRef = useRef(false);
  const handleDetailChanged = useCallback((lead) => {
    detailDirtyRef.current = true;
    upsertLead(lead);
  }, [upsertLead]);
  const handleDetailBack = useCallback(() => {
    setOpenLeadId('');
    if (detailDirtyRef.current) {
      detailDirtyRef.current = false;
      reload();
    }
  }, [reload]);

  const removeLead = useCallback((leadId) => {
    setLeads((prev) => prev.filter((l) => l.id !== leadId));
  }, []);

  const handleGone = useCallback((leadId) => {
    removeLead(leadId);
    setOpenLeadId((cur) => (cur === leadId ? '' : cur));
    reload();
  }, [removeLead, reload]);

  const handleDelete = async (lead) => {
    setMenu(null);
    if (!canDelete || !lead?.id) return;
    const answer = await confirmDialog({
      title: '¿Archivar este lead?',
      description: `${lead.name || 'El lead'} sale de la lista; sus consultas, notas e historial quedan. Si vuelve a consultar, se desarchiva solo.`,
      confirmLabel: 'Archivar',
      cancelLabel: 'Cancelar',
      variant: 'warning',
    });
    if (!answer?.isConfirmed) return;
    deleteAbortRef.current?.abort();
    const controller = new AbortController();
    deleteAbortRef.current = controller;
    setDeletingId(lead.id);
    try {
      const res = await deleteLead(lead.id, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (res?._httpOk || res?._httpStatus === 404) {
        toast.success('Lead archivado');
        handleGone(lead.id);
      } else {
        toast.error(getLeadErrorMessage(res, 'No se pudo archivar el lead.'));
      }
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo archivar el lead.');
    } finally {
      if (deleteAbortRef.current === controller) setDeletingId('');
    }
  };

  const handleCreated = (lead) => {
    setCreateOpen(false);
    toast.success(`Lead creado: ${lead.name}`);
    upsertLead(lead);
    reload();
    setOpenLeadId(lead.id);
  };

  // Consulta manual: refresca lista / contadores / resumen y abre el lead
  // (la consulta queda en su historial y en «Intereses y consultas»).
  const handleManualCreated = (result) => {
    setManualOpen(false);
    toast.success(describeManualInquiryCreated(result));
    reload();
    if (result?.leadId) setOpenLeadId(result.leadId);
  };

  const handleOpenExisting = (leadId) => {
    setCreateOpen(false);
    setOpenLeadId(leadId);
  };

  const hasFilters = Boolean(query) || temperatureFilter !== 'all' || followUpActive
    || advancedActive || Boolean(recordFilter);
  const advancedCount = countLeadAdvancedFilters(appliedFilters);

  // Resumen (izq. de “Ordenar por”). Total = stats del owner; consultas sin ver
  // = suma de lo cargado (≤ PAGE_LIMIT). Temperatura sola con has_more →
  // by_temperature (exacto); búsqueda / seguimiento con has_more → “200+”.
  const summaryTotal = serverStats ? serverCounts.total : leads.length;
  const temperatureOnlyBeyondPage = hasMore && temperatureFilter !== 'all' && !query && !followUpActive
    && !advancedActive && !recordFilter;
  // `total_filtered` (API) = exacto con filtros de servidor (incluye temperature=).
  const exactFiltered = totalFiltered !== null && hasFilters;
  const summaryVisible = exactFiltered ? totalFiltered
    : (temperatureOnlyBeyondPage ? counts.byTemperature[temperatureFilter] : visibleLeads.length);
  const summaryUnseen = useMemo(
    () => leads.reduce((acc, l) => acc + (Number(l.unseen_inquiry_count) > 0 ? Math.trunc(l.unseen_inquiry_count) : 0), 0),
    [leads],
  );
  const summaryFilterLabels = [
    describeLeadRecordFilter(recordFilter),
    temperatureFilter !== 'all' ? LEAD_TEMPERATURE_LABELS[temperatureFilter] : '',
    followUpLabel,
    ...describeLeadAdvancedFilters(appliedFilters),
    query ? `“${query}”` : '',
  ].filter(Boolean);
  const clearAdvancedFilters = () => {
    setAdvancedFilters(EMPTY_LEAD_ADVANCED_FILTERS);
    setAppliedFilters(EMPTY_LEAD_ADVANCED_FILTERS);
  };
  // Cambiar de modo limpia el texto; el chip de registro activo se mantiene.
  const changeSearchMode = (next) => {
    setModeAnchor(null);
    if (next === searchMode) return;
    setSearchMode(next);
    setSearch('');
    setQuery('');
    setRecordText('');
  };
  const pickRecord = (next) => {
    setRecordFilter(next);
    setRecordText('');
  };
  const isRecordMode = searchMode === 'record';
  const clearFilters = () => {
    clearRecordFilter();
    setTemperatureFilter('all');
    setFollowUpFilter('all');
    setSearch('');
    setQuery('');
    setRecordText('');
    setAdvancedFilters(EMPTY_LEAD_ADVANCED_FILTERS);
    setAppliedFilters(EMPTY_LEAD_ADVANCED_FILTERS);
  };
  // Skeleton hasta que termina la primera consulta (con data, vacía o error).
  // `fetching` arranca en false hasta el efecto: no puede decidir el primer
  // render (se veía el empty state antes del skeleton). El 404 RECORD_NOT_FOUND
  // no marca `loaded`: sigue el skeleton hasta el refetch sin filtro.
  const showInitialSkeleton = !loaded;
  const menuLead = menu?.lead || null;

  const closeButton = (
    <IconButton onClick={onClose} size="small" aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
      <CloseIcon />
    </IconButton>
  );

  const renderIndexBody = () => {
    if (showInitialSkeleton) {
      return <LeadCardsSkeleton count={5} withMenu={canDelete} />;
    }
    if (forbidden) {
      return (
        <Box sx={emptyStateBoxSx} data-testid="leads-forbidden">
          <Box sx={emptyStateIconBoxSx}>
            <LockOutlinedIcon sx={emptyStateIconSx} />
          </Box>
          <Typography sx={emptyStateTitleSx}>No tenés acceso a los leads de este espacio.</Typography>
          <Typography sx={emptyStateHintSx}>
            Los leads los ven el dueño y quienes editan el espacio completo.
          </Typography>
        </Box>
      );
    }
    if (loadError && !leads.length) {
      return (
        <Box sx={emptyStateBoxSx}>
          <Box sx={emptyStateIconBoxSx}>
            <ErrorOutlineIcon sx={emptyStateIconSx} />
          </Box>
          <Button onClick={reload} sx={{ ...leadsGhostButtonSx, mt: 0.5 }}>Reintentar</Button>
        </Box>
      );
    }
    if (!visibleLeads.length) {
      return hasFilters ? (
        <PanelEmptyState
          icon={SearchIcon}
          title="No hay leads que coincidan."
          hint="Probá otra búsqueda, nivel o seguimiento."
        />
      ) : (
        <PanelEmptyState
          icon={PersonAddOutlinedIcon}
          title="Todavía no hay leads."
          hint="Cargá a quien te consultó y seguilo etapa por etapa hasta cerrar."
          actionLabel="Crear lead"
          onAction={() => setCreateOpen(true)}
        />
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="leads-list">
        {visibleLeads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            busy={deletingId === lead.id}
            onOpen={setOpenLeadId}
            onOpenMenu={canDelete ? ((anchor, l) => setMenu({ anchor, lead: l })) : null}
          />
        ))}
        {hasMore && (
          <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED, textAlign: 'center', py: 1 }}>
            {totalFiltered !== null
              ? `Mostrando ${PAGE_LIMIT} de ${totalFiltered}. Usá la búsqueda o los filtros para encontrar otros.`
              : `Mostrando los ${PAGE_LIMIT} más recientes. Usá la búsqueda para encontrar otros.`}
          </Typography>
        )}
      </Box>
    );
  };

  const returnsToUnit = Boolean(unitReturn && onUnitReturn && openLeadId === unitReturn.leadId);

  return (
    <PanelShell uiLeftInset={uiLeftInset} onClose={onClose}>
      {({ snap }) => (
        <Paper elevation={0} sx={leadsPanelPaperSx} data-testid="leads-panel">
          {openLeadId ? (
            <LeadDetail
              key={openLeadId}
              leadId={openLeadId}
              ownerUserId={ownerUserId}
              canDelete={canDelete}
              cardUnseenInquiries={leads.find((l) => l.id === openLeadId)?.unseen_inquiry_count || 0}
              onBack={returnsToUnit ? onUnitReturn : handleDetailBack}
              backLabel={returnsToUnit ? `Volver a ${unitReturn.label}` : undefined}
              onClose={onClose}
              onChanged={handleDetailChanged}
              onDeleted={(id) => {
                handleGone(id);
                setOpenLeadId('');
              }}
              onGone={handleGone}
              onOpenLead={setOpenLeadId}
              onOpenItemTarget={onOpenItemTarget}
            />
          ) : (
            <>
              <Box sx={{ bgcolor: LEADS_PANEL_BG }}>
                <PanelHeader
                  title="LEADS"
                  subtitle={LEADS_SUBTITLE}
                  actions={closeButton}
                />
              </Box>

              {snap !== 'collapsed' && !forbidden && !(loadError && !leads.length) && (
                <Box sx={leadsToolbarZoneSx} aria-busy={showInitialSkeleton || undefined}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }} data-testid="leads-search">
                      <TextField
                        value={isRecordMode ? recordText : search}
                        onChange={(e) => (isRecordMode ? setRecordText(e.target.value) : setSearch(e.target.value))}
                        onKeyDown={(e) => { if (e.key === 'Escape' && isRecordMode) setRecordText(''); }}
                        // Ejemplo que llena ~80–85 % del texto disponible en el panel de 520 px
                        // (~203 px; Inter 14 px: 161 / 171 px, Arial 178 / 192 px). Más angosto: «…».
                        // El label accesible dice qué busca.
                        placeholder={isRecordMode ? 'Ej: Depto 14E, Rondeau 349' : 'Ej: Juan Pérez o 351 555 1234'}
                        size="small"
                        fullWidth
                        inputProps={{
                          'aria-label': isRecordMode ? 'Buscar registros' : 'Buscar leads',
                          maxLength: 120,
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.5, flexShrink: 0 }}>
                              <Button
                                size="small"
                                onClick={(e) => setModeAnchor(e.currentTarget)}
                                aria-label={`Buscar en: ${isRecordMode ? 'Registros' : 'Leads'}`}
                                aria-haspopup="menu"
                                aria-expanded={Boolean(modeAnchor)}
                                endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                  // Ancho fijo al label más largo ("Registros") para que el input no salte.
                                  // Nunca se encoge ni corta el texto: lo que cede es el input.
                                  // space-between: la flecha queda fija a la derecha al cambiar Leads/Registros.
                                  width: 115,
                                  minWidth: 115,
                                  flexShrink: 0,
                                  ml: -0.75,
                                  px: 0.75,
                                  py: 0.25,
                                  gap: 0,
                                  textTransform: 'none',
                                  color: colors.textSecondary,
                                  fontFamily: typography.fontFamily.primary,
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  whiteSpace: 'nowrap',
                                  justifyContent: 'space-between',
                                  borderRight: '1px solid rgba(255,255,255,0.12)',
                                  borderRadius: '6px 0 0 6px',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: colors.textPanelTitle },
                                  '& .MuiButton-endIcon': { ml: 0.25, mr: 0, flexShrink: 0 },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: 1,
                                  }}
                                >
                                  <SearchIcon sx={{ ...listSearchIconSx, mr: 0.5, flexShrink: 0 }} />
                                  <Box
                                    component="span"
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      textAlign: 'left',
                                      color: colors.textPanelTitle,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {isRecordMode ? 'Registros' : 'Leads'}
                                  </Box>
                                </Box>
                              </Button>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end" sx={{ ml: 0.25, flexShrink: 0 }}>
                              <Tooltip title="Filtros" placement="top" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => setFiltersAnchor(e.currentTarget)}
                                  aria-label="Filtros de leads"
                                  aria-haspopup="dialog"
                                  aria-expanded={Boolean(filtersAnchor)}
                                  sx={{
                                    position: 'relative',
                                    width: 28,
                                    height: 28,
                                    p: 0.25,
                                    borderRadius: '6px',
                                    color: filtersAnchor || advancedCount ? colors.primary : LEADS_TEXT_SECONDARY,
                                    '&:hover': {
                                      bgcolor: 'rgba(255,255,255,0.06)',
                                      color: filtersAnchor || advancedCount ? colors.primary : colors.textPanelTitle,
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                  }}
                                >
                                  <TuneIcon />
                                  {advancedCount > 0 && (
                                    <span
                                      style={{ ...filterBadgeStyle, bottom: -3, right: -3 }}
                                      data-testid="leads-filters-count"
                                    >
                                      {advancedCount}
                                    </span>
                                  )}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          ...listSearchFieldSx,
                          mt: 0,
                          // Conservar borde / radio / fondo de listSearchFieldSx; solo ajustar padding
                          // por el botón Filtros al final.
                          '& .MuiOutlinedInput-root': {
                            ...listSearchFieldSx['& .MuiOutlinedInput-root'],
                            pr: '6px',
                          },
                          '& .MuiOutlinedInput-input': {
                            ...listSearchFieldSx['& .MuiOutlinedInput-input'],
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                      <Menu
                        anchorEl={modeAnchor}
                        open={Boolean(modeAnchor)}
                        onClose={() => setModeAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                          sx: {
                            bgcolor: colors.backgroundDark,
                            backgroundImage: 'none',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            minWidth: 140,
                          },
                        }}
                      >
                        <MenuItem
                          selected={!isRecordMode}
                          onClick={() => changeSearchMode('people')}
                          sx={{
                            color: colors.textPrimary,
                            fontSize: '13px',
                            fontFamily: typography.fontFamily.primary,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
                          }}
                        >
                          Leads
                        </MenuItem>
                        <MenuItem
                          selected={isRecordMode}
                          onClick={() => changeSearchMode('record')}
                          sx={{
                            color: colors.textPrimary,
                            fontSize: '13px',
                            fontFamily: typography.fontFamily.primary,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
                          }}
                        >
                          Registros
                        </MenuItem>
                      </Menu>
                      {isRecordMode && recordText.trim() ? (
                        <LeadRecordSearchResults text={recordText} ownerUserId={ownerUserId} onPick={pickRecord} />
                      ) : null}
                    </Box>
                    <Tooltip title="Nuevo lead" placement="top" arrow>
                      <IconButton
                        onClick={() => setCreateOpen(true)}
                        aria-label="Nuevo lead"
                        sx={{
                          ...listAddButtonSx,
                          width: 44,
                          height: 44,
                          minWidth: 44,
                          borderRadius: '8px',
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    {!isManualInquiryDisabled() && (
                      <Tooltip title="Nueva consulta" placement="top" arrow>
                        <IconButton
                          onClick={() => setManualOpen(true)}
                          aria-label="Nueva consulta"
                          sx={{
                            ...listAddButtonSx,
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            borderRadius: '8px',
                            '& .MuiSvgIcon-root': { fontSize: 20 },
                          }}
                        >
                          <AddCommentOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <LeadsFiltersPanel
                    anchorEl={filtersAnchor}
                    open={Boolean(filtersAnchor)}
                    onClose={() => setFiltersAnchor(null)}
                    value={advancedFilters}
                    onChange={setAdvancedFilters}
                    activeCount={advancedCount}
                    onClear={clearAdvancedFilters}
                    showProfile={showProfileFilters}
                  />
                  {recordFilter && (
                    <Box sx={{ display: 'flex', minWidth: 0 }} data-testid="leads-record-filter">
                      <Chip
                        size="small"
                        label={describeLeadRecordFilter(recordFilter)}
                        title={describeLeadRecordFilter(recordFilter)}
                        onDelete={clearRecordFilter}
                        deleteIcon={<CloseIcon aria-label="Quitar filtro de registro" />}
                        sx={{ ...leadItemChipSx, minWidth: 0 }}
                      />
                    </Box>
                  )}
                  {showInitialSkeleton ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        width: '100%',
                        minWidth: 0,
                      }}
                      aria-hidden
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: LEADS_TEXT_MUTED,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Nivel de interés
                      </Typography>
                      <LeadsTemperatureChipsSkeleton />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        width: '100%',
                        minWidth: 0,
                      }}
                      role="group"
                      aria-label="Filtrar por nivel de interés"
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: LEADS_TEXT_MUTED,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Nivel de interés
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flex: '1 1 0',
                          minWidth: 0,
                        }}
                      >
                        <Button
                          size="small"
                          aria-pressed={temperatureFilter === 'all'}
                          onClick={() => setTemperatureFilter('all')}
                          sx={{
                            ...getLeadTemperatureFilterChipSx(temperatureFilter === 'all', 'all'),
                            flex: '1 1 0',
                            minWidth: 0,
                            justifyContent: 'center',
                          }}
                        >
                          Todos
                          <Box component="span" sx={leadsCountBadgeSx}>{counts.total}</Box>
                        </Button>
                        {LEAD_TEMPERATURES.map((temp) => (
                          <Button
                            key={temp}
                            size="small"
                            aria-pressed={temperatureFilter === temp}
                            onClick={() => setTemperatureFilter((cur) => (cur === temp ? 'all' : temp))}
                            sx={{
                              ...getLeadTemperatureFilterChipSx(temperatureFilter === temp, temp),
                              flex: '1 1 0',
                              minWidth: 0,
                              justifyContent: 'center',
                            }}
                          >
                            {LEAD_TEMPERATURE_LABELS[temp]}
                            <Box component="span" sx={leadsCountBadgeSx}>{counts.byTemperature[temp]}</Box>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }} role="group" aria-label="Filtrar por seguimiento">
                    <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED, mr: 0.5 }}>Seguimiento</Typography>
                    {LEAD_FOLLOWUP_FILTER_OPTIONS.map((option) => {
                      const active = followUpFilter === option.id;
                      const badge = option.id === 'all'
                        ? serverCounts.total
                        : (option.id === 'unseen' ? serverCounts.unseen : null);
                      return (
                        <Button
                          key={option.id}
                          size="small"
                          aria-pressed={active}
                          onClick={() => setFollowUpFilter(option.id)}
                          sx={{
                            // Mismo estilo que los chips de Origen (HEAD): pill 24px,
                            // sin borde de acento (distinto de Nivel de interés).
                            textTransform: 'none',
                            minWidth: 0,
                            height: 24,
                            px: 1,
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: active ? 600 : 500,
                            color: active ? '#FFFFFF' : LEADS_TEXT_SECONDARY,
                            bgcolor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                            gap: badge != null ? '6px' : 0,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                          }}
                        >
                          {option.label}
                          {badge != null ? (
                            <Box component="span" sx={leadsCountBadgeSx}>{badge}</Box>
                          ) : null}
                        </Button>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minWidth: 0 }}>
                    {/* «Ordenar por» no depende de la data: siempre visible. El resumen sí. */}
                    {showInitialSkeleton ? <LeadsSummarySkeleton /> : null}
                    {!showInitialSkeleton && (summaryTotal > 0 || hasFilters) && (
                      <LeadsSummary
                        total={summaryTotal}
                        visible={summaryVisible}
                        visibleIsLowerBound={hasMore && !temperatureOnlyBeyondPage && !exactFiltered && hasFilters}
                        unseenInquiries={summaryUnseen}
                        filterLabels={summaryFilterLabels}
                        onClear={clearFilters}
                      />
                    )}
                    <Button
                      size="small"
                      onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                      endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
                      aria-label="Ordenar leads"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(sortMenuAnchor)}
                      sx={{
                        textTransform: 'none',
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily.primary,
                        fontSize: '12px',
                        fontWeight: 400,
                        minHeight: 24,
                        px: 0.5,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: colors.textPanelTitle },
                        '& .MuiButton-endIcon': { ml: 0.25 },
                      }}
                    >
                      Ordenar por:
                      <Box component="span" sx={{ color: colors.textPanelTitle, fontWeight: 500, ml: 0.5 }}>
                        {LEAD_SORT_LABELS[sortBy]}
                      </Box>
                    </Button>
                    <Menu
                      anchorEl={sortMenuAnchor}
                      open={Boolean(sortMenuAnchor)}
                      onClose={() => setSortMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{
                        sx: {
                          bgcolor: colors.backgroundDark,
                          backgroundImage: 'none',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          minWidth: 180,
                        },
                      }}
                    >
                      {Object.entries(LEAD_SORT_LABELS).map(([value, label]) => (
                        <MenuItem
                          key={value}
                          selected={sortBy === value}
                          onClick={() => {
                            setSortBy(value);
                            setSortMenuAnchor(null);
                          }}
                          sx={{
                            color: colors.textPrimary,
                            fontSize: '13px',
                            fontFamily: typography.fontFamily.primary,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
                          }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </Box>
              )}

              <Box sx={{ height: 2, flexShrink: 0 }}>
                {fetching && loaded && (
                  <LinearProgress sx={{ height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: '#489291' } }} />
                )}
              </Box>

              <Box
                ref={scrollRef}
                data-testid="leads-scroll"
                sx={{
                  ...scrollableListZoneSx,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: LEADS_PANEL_BG,
                  px: 2.5,
                  py: 1.5,
                  ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
                }}
              >
                {renderIndexBody()}
              </Box>
            </>
          )}

          <Menu
            anchorEl={menu?.anchor || null}
            open={Boolean(menu)}
            onClose={() => setMenu(null)}
            PaperProps={{ ...ACTION_MENU_PAPER_PROPS, sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 190, minWidth: 190 } }}
            MenuListProps={ACTION_MENU_LIST_PROPS}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuLead && canDelete && (
              <ActionMenuItem
                icon={<ArchiveOutlinedIcon fontSize="small" />}
                label="Archivar lead"
                onClick={() => handleDelete(menuLead)}
              />
            )}
          </Menu>
          <LeadCreateDialog
            open={createOpen}
            ownerUserId={ownerUserId}
            onClose={() => setCreateOpen(false)}
            onCreated={handleCreated}
            onOpenExisting={handleOpenExisting}
          />
          <LeadManualInquiryDialog
            open={manualOpen}
            ownerUserId={ownerUserId}
            onClose={() => setManualOpen(false)}
            onCreated={handleManualCreated}
          />
        </Paper>
      )}
    </PanelShell>
  );
}
